import { ISignupNames } from "src/Module/Checkout/socialSignupNames";

const USERINFO_URL = "https://www.googleapis.com/oauth2/v3/userinfo";

interface IGoogleProfile {
    given_name?: string;
    family_name?: string;
    name?: string;
}

/**
 * Pull a first and last name out of Google's userinfo payload.
 *
 * `given_name` and `family_name` are the reliable pair, but a Google account can
 * carry only the single display `name` — for those, the first word is taken as
 * the given name and everything after it as the family name, which is the same
 * split Google itself applies when it has to.
 */
export const parseGoogleProfileNames = (profile?: IGoogleProfile | null): ISignupNames | null => {
    const given = profile?.given_name?.trim();
    const family = profile?.family_name?.trim();
    if (given && family) return { first_name: given, last_name: family };

    const whole = profile?.name?.trim();
    if (whole) {
        const parts = whole.split(/\s+/);
        if (parts.length > 1) {
            return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
        }
    }

    return null;
};

/**
 * Ask Google who just signed in.
 *
 * Uses the provider's own access token, which the `openid profile email` scope
 * already covers, so no backend change and nothing for the visitor to do. Plain
 * `fetch` rather than one of the app's axios clients: those attach app
 * credentials and a 401 from them triggers a refresh-or-logout, neither of which
 * belongs on a third-party host.
 */
export const fetchGoogleProfileNames = async (
    googleAccessToken: string,
): Promise<ISignupNames | null> => {
    try {
        const response = await fetch(USERINFO_URL, {
            headers: { Authorization: `Bearer ${googleAccessToken}` },
        });
        if (!response.ok) return null;
        return parseGoogleProfileNames(await response.json());
    } catch (error) {
        // The sign-in itself has already succeeded; a missing name only costs the
        // points upgrade, which is handled by the caller.
        console.error("Could not read the Google profile:", error);
        return null;
    }
};
